import { FormControl, FormGroup } from '@angular/forms';
import {
    ApplyBookingResponseDto,
    BookingSummaryDto,
    PartyDetailDto,
} from '@opb-workspace/shared';
import { InstructivoEmbarqueStateService } from '../../../services/instructivo-embarque-state.service';
import {
    bookingNumberDisplayFromSummary,
    coerceBookingSummaryLike,
} from './step2-booking-summary.util';

function resolveBookingNumberForWizardState(
    form: FormGroup,
    bookingInputCtrl: FormControl<string | BookingSummaryDto> | undefined,
    availableBookings: BookingSummaryDto[] | undefined,
    snapshotBookingNumber: string | undefined,
): string | undefined {
    let fromInput = '';
    if (bookingInputCtrl) {
        const raw = bookingInputCtrl.value;
        if (typeof raw === 'string') {
            fromInput = raw.trim();
        } else if (raw != null && availableBookings?.length) {
            const row = coerceBookingSummaryLike(raw, availableBookings);
            if (row) {
                fromInput = bookingNumberDisplayFromSummary(row).trim();
            }
        }
    }
    const fromForm = (form.get('bookingNumber')?.value ?? '')
        .toString()
        .trim();
    const fromSnap = (snapshotBookingNumber ?? '').trim();
    const merged = fromInput || fromForm || fromSnap;
    return merged || undefined;
}

function trimOrEmpty(v: unknown): string {
    if (v == null) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' || typeof v === 'boolean') {
        return String(v).trim();
    }
    return '';
}

function firstNonEmpty(...vals: Array<string | undefined | null>): string | undefined {
    for (const x of vals) {
        const t = (x ?? '').toString().trim();
        if (t.length) return t;
    }
    return undefined;
}

function partyFromBooking(
    booking: ApplyBookingResponseDto | null | undefined,
    role: 'shipper' | 'consignee' | 'notify',
): PartyDetailDto | undefined {
    if (!booking) {
        return undefined;
    }
    if (role === 'shipper') {
        return booking.shipper;
    }
    if (role === 'consignee') {
        return booking.consignee;
    }
    return booking.notify;
}

/** Evita perder RUT/contacto del apply-booking si el formulario no los expone en `.value` (p. ej. controles deshabilitados). */
function mergePartyContactFields(
    formVals: Record<string, unknown>,
    booking: ApplyBookingResponseDto | null | undefined,
    existing: Record<string, unknown>,
    role: 'shipper' | 'consignee' | 'notify',
    keys: {
        email: string;
        tax: string;
        contact: string;
        phone: string;
        mobile: string;
    },
): {
    email?: string;
    tax?: string;
    contact?: string;
    phone?: string;
    mobile?: string;
} {
    const p = partyFromBooking(booking, role);
    return {
        email: firstNonEmpty(
            trimOrEmpty(formVals[keys.email]),
            p?.email,
            trimOrEmpty(existing[keys.email]),
        ),
        tax: firstNonEmpty(
            trimOrEmpty(formVals[keys.tax]),
            p?.taxIdentificationNumber,
            trimOrEmpty(existing[keys.tax]),
        ),
        contact: firstNonEmpty(
            trimOrEmpty(formVals[keys.contact]),
            p?.contactName,
            trimOrEmpty(existing[keys.contact]),
        ),
        phone: firstNonEmpty(
            trimOrEmpty(formVals[keys.phone]),
            p?.phoneNumber,
            trimOrEmpty(existing[keys.phone]),
        ),
        mobile: firstNonEmpty(
            trimOrEmpty(formVals[keys.mobile]),
            p?.mobilePhoneNumber,
            trimOrEmpty(existing[keys.mobile]),
        ),
    };
}

export function pushStep2FormToWizardState(
    state: InstructivoEmbarqueStateService,
    form: FormGroup,
    bookingInputCtrl?: FormControl<string | BookingSummaryDto>,
    availableBookings?: BookingSummaryDto[],
): void {
    const v = form.getRawValue() as Record<string, unknown>;
    const snap = state.snapshot;
    const bd = snap.bookingData;
    const fd = snap.formData as Record<string, unknown>;
    const bookingNumber = resolveBookingNumberForWizardState(
        form,
        bookingInputCtrl,
        availableBookings,
        bd?.bookingNumber,
    );

    const shipperContact = mergePartyContactFields(v, bd, fd, 'shipper', {
        email: 'shipperEmail',
        tax: 'shipperTaxIdentificationNumber',
        contact: 'shipperContactName',
        phone: 'shipperPhoneNumber',
        mobile: 'shipperMobilePhoneNumber',
    });

    state.updateFormData({
        bookingId: (v['selectedBookingId'] as number | null) || undefined,
        bookingNumber,
        // Ref. Cliente: por-instructivo desde feat-ie-wizard-mejoras (punto 2) — ya no se lee
        // del form de Step2, ver `InstructivoEmbarqueData.customerReference` por panel (Step 3).
        customerContactEmail: firstNonEmpty(
            trimOrEmpty(v['customerContactEmail']),
            bd?.customerContactEmail,
            trimOrEmpty(fd['customerContactEmail']),
        ),
        customerAttentionContactName: firstNonEmpty(
            trimOrEmpty(v['customerAttentionContactName']),
            bd?.customerAttentionContactName,
            trimOrEmpty(fd['customerAttentionContactName']),
        ),
        customerTaxIdentificationNumber: firstNonEmpty(
            trimOrEmpty(v['customerTaxIdentificationNumber']),
            bd?.customerTaxIdentificationNumber,
            trimOrEmpty(fd['customerTaxIdentificationNumber']),
        ),
        shipperPartyId: (v['shipperPartyId'] as number | null | undefined) ??
            undefined,
        shipperName: (v['shipperName'] as string) ?? '',
        shipperAddress: (v['shipperAddress'] as string) ?? '',
        shipperEmail: shipperContact.email,
        shipperTaxIdentificationNumber: shipperContact.tax,
        shipperContactName: shipperContact.contact,
        shipperPhoneNumber: shipperContact.phone,
        shipperMobilePhoneNumber: shipperContact.mobile,
    });
}
